import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Well } from "@zendeskgarden/react-notifications";
import { Tag } from "@zendeskgarden/react-tags";
import { useNavigate, useLocation } from "react-router-dom";
import { casesApi } from "../../api";
import { useAuth } from "../../utils/auth";
import FilterableTable, { Column } from "../common/FilterableTable";
import DashboardLayout from "../common/DashboardLayout";
import Profile from "../common/Profile";
import HelpSupport from "../common/HelpSupport";
import { CasesIcon, ReviewIcon, ProfileIcon, HelpSupportIcon } from "../common/Icons";
import ReviewerCases from "./ReviewerCases";
import ReviewerMyCases from "./ReviewerMyCases";
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled(Well)`
  padding: 24px;
  text-align: center;
  border-radius: 8px;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: #8D4F1B;
  margin-bottom: 8px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const StatLabel = styled.div`
  color: #68737d;
  font-size: 14px;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SectionTitle = styled.h2`
  color: #2f3941;
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 600;
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
`;

interface Case {
  id: string;
  doctorDiagnosis: string;
  doctorNote: string;
  reviewed: boolean;
  reviewedDiagnosis: string;
  reviewerNote: string;
}

const ReviewerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function fetchCases() {
      setLoading(true);
      try {
        const res = await casesApi.getMyCases();
        const transformedCases = (res.data.cases || res.data).map((caseData: any) => ({
          id: caseData._id,
          doctorDiagnosis: caseData.doctorDiagnosis,
          doctorNote: caseData.doctorNote,
          reviewed: caseData.reviewStatus === 'approved' || caseData.reviewStatus === 'rejected',
          reviewedDiagnosis: caseData.reviewNote || '',
          reviewerNote: caseData.reviewNote || ''
        }));
        setCases(transformedCases);
      } catch (e) {
        console.error("Failed to fetch cases:", e);
      }
      setLoading(false);
    }
    fetchCases();
  }, []);

  useEffect(() => {
    // Set active view based on current path
    const path = location.pathname;
    if (path.includes('/my-cases')) {
      setActiveView('my-cases');
    } else if (path.includes('/review-cases')) {
      setActiveView('review-cases');
    } else if (path.includes('/profile')) {
      setActiveView('profile');
    } else if (path.includes('/help')) {
      setActiveView('help');
    } else {
      setActiveView('dashboard');
    }
  }, [location.pathname]);

  const stats = {
    totalCases: cases.length,
    reviewedCases: cases.filter(c => c.reviewed).length,
    pendingCases: cases.filter(c => !c.reviewed).length,
  };

  const getPageTitle = () => {
    switch (activeView) {
      case 'my-cases':
        return 'My Cases';
      case 'review-cases':
        return 'Review Cases';
      case 'profile':
        return 'My Profile';
      case 'help':
        return 'Help & Support';
      default:
        return 'Reviewer Dashboard';
    }
  };

  const getPageDescription = () => {
    switch (activeView) {
      case 'my-cases':
        return 'Manage your submitted cases and track their review status.';
      case 'review-cases':
        return 'Review and validate AI-generated diagnoses from physicians.';
      case 'profile':
        return 'Manage your profile settings and preferences.';
      case 'help':
        return 'Get help and support for using the platform.';
      default:
        return 'Monitor your review activities and case statistics.';
    }
  };

  const menuItems = [
    {
      id: 'my-cases',
      label: 'My Cases',
      icon: <CasesIcon />,
      path: '/reviewer/my-cases'
    },
    {
      id: 'review-cases',
      label: 'Review Cases',
      icon: <ReviewIcon />,
      path: '/reviewer/review-cases'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: <ProfileIcon />,
      path: '/reviewer/profile'
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: <HelpSupportIcon />,
      path: '/reviewer/help'
    }
  ];

  // Define table columns for recent reviews
  const recentReviewColumns: Column[] = [
    { key: 'id', title: 'Case ID' },
    { key: 'doctorDiagnosis', title: 'AI Diagnosis' },
    { key: 'reviewedDiagnosis', title: 'My Review' },
    { 
      key: 'reviewed', 
      title: 'Status',
      render: (value) => (
        <Tag hue={value ? 'green' : 'orange'} size="small">
          {value ? 'Completed' : 'Pending'}
        </Tag>
      )
    },
    { key: 'reviewerNote', title: 'Notes' }
  ];

  const renderContent = () => {
    switch (activeView) {
      case 'my-cases':
        return <ReviewerMyCases />;
      case 'review-cases':
        return <ReviewerCases />;
      case 'profile':
        return <Profile />;
      case 'help':
        return <HelpSupport />;
      default:
        return (
          <>
            <SectionTitle>Review Statistics</SectionTitle>
            {/* Statistics Overview */}
            <StatsGrid>
              <StatCard>
                <StatNumber>{stats.totalCases}</StatNumber>
                <StatLabel>Cases Assigned</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{stats.reviewedCases}</StatNumber>
                <StatLabel>Reviews Completed</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{stats.pendingCases}</StatNumber>
                <StatLabel>Pending Reviews</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>98.5%</StatNumber>
                <StatLabel>Review Accuracy</StatLabel>
              </StatCard>
            </StatsGrid>

            {/* Recent Reviews Table */}
            <FilterableTable
              title="Recent Reviews"
              columns={recentReviewColumns}
              data={cases.slice(0, 10)}
              loading={loading}
              searchPlaceholder="Search recent reviews..."
              searchFields={['id', 'doctorDiagnosis', 'reviewedDiagnosis', 'reviewerNote']}
              maxRows={10}
            />
          </>
        );
    }
  };

  return (
    <DashboardLayout
      title={getPageTitle()}
      userInfo={`${user?.name || 'Dr. Medical Reviewer'} • ${user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Specialist Reviewer'}`}
      activeTab={activeView}
      menuItems={menuItems}
      onTabChange={(tabId) => {
        const item = menuItems.find(m => m.id === tabId);
        if (item) {
          navigate(item.path);
        }
      }}
      userName={user?.name || 'Dr. Medical Reviewer'}
      userType={user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Reviewer'}
      pageDescription={getPageDescription()}
    >
      {renderContent()}
    </DashboardLayout>
  );
};

export default ReviewerDashboard;
